import { Box, Divider, Grid, Tooltip } from '@material-ui/core';
import { SButton, SHighlightText, SText } from '@setvi/shared/components';
import { useDialog } from '@setvi/shared/providers';
import { valueToMonetary } from 'pages/products/utils';
import { useRFQStore } from 'pages/auto-rfq/store';
import { AnalyticsModules, useAnalytics } from '@setvi/shared/hooks';
import { ProductCardProps } from '../..';
import ProductSpecifications from '../specifications';
import { FlexCol, FlexRow, iconStyle } from '../../styled';

const Competitor = ({
  desc,
  order,
  search,
  matched,
  summary,
  quantity,
  unitPrice,
  productName,
  manufacturer,
  competitorSku,
  manufacturerSku,
  customAttributes,
  fileUrl,
  attributeLineNo,
  excelRow,
  originalOrder,
  expandedView = true
}: Partial<ProductCardProps>) => {
  const { openDialog, closeDialog } = useDialog();
  const { captureAnalytics } = useAnalytics();
  const { params, files } = useRFQStore();

  const fileSource = files?.find(f => f?.file?.url === fileUrl)?.file?.name;
  const originalOrderNo = attributeLineNo || excelRow || order || originalOrder;

  if (!expandedView) {
    return (
      <Grid item xs>
        <FlexRow compact>
          <Box
            style={{
              display: 'flex',
              flexDirection: 'row',
              gap: 12,
              alignItems: 'flex-start'
            }}>
            <Box
              style={{
                borderRadius: '50%',
                background: '#E1E5EA',
                width: 26,
                height: 26,
                minWidth: 26,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginTop: 2
              }}>
              <SText
                size="sm"
                fontColor="#5D7083"
                title={`${order}`}
                noLineClamp
              />
            </Box>

            <Box
              style={{
                display: 'flex',
                flexDirection: 'column',
                flex: 1,
                gap: 4,
                minWidth: 0
              }}>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                gridGap={12}>
                <Box
                  style={{
                    flex: 1,
                    minWidth: 0,
                    overflow: 'hidden'
                  }}>
                  <SHighlightText
                    value={`${productName || desc || '-'}`}
                    searchQuery={search}
                    maxLines={1}
                    fontColor="#333D47"
                    size="md"
                    weight="bold"
                    style={{ width: '100%' }}
                  />
                </Box>
                {!!unitPrice && (
                  <Box
                    style={{
                      flexShrink: 0,
                      maxWidth: 200,
                      overflow: 'hidden'
                    }}>
                    <SHighlightText
                      value={`Price: ${Number(unitPrice) ? `${valueToMonetary(Number(unitPrice))}` : unitPrice || '-'}`}
                      searchQuery={search}
                      size="sm"
                      maxLines={1}
                      weight="bold"
                      fontColor="#333D47"
                      style={{ width: '100%' }}
                    />
                  </Box>
                )}
              </Box>

              <Box display="flex" alignItems="center" gridGap={8}>
                <SHighlightText
                  value={`SKU: ${competitorSku ? `#${competitorSku}` : '-'}`}
                  searchQuery={search}
                  size="sm"
                  maxLines={1}
                  fontColor="#5D7083"
                />
                <Box component="span" style={{ color: '#5D7083' }}>
                  •
                </Box>
                <SHighlightText
                  value={`MFG: ${manufacturer || ''} ${manufacturerSku ? `(${manufacturerSku})` : '-'}`}
                  searchQuery={search}
                  size="sm"
                  maxLines={1}
                  fontColor="#5D7083"
                />
              </Box>
            </Box>
          </Box>
        </FlexRow>
      </Grid>
    );
  }

  return (
    <Grid item xs>
      <FlexRow>
        <Box
          style={{
            display: 'flex',
            flexDirection: 'row',
            gap: 12,
            height: '100%'
          }}>
          <Box>
            <Box
              style={{
                borderRadius: '50%',
                background: '#E1E5EA',
                width: 26,
                height: 26,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
              <SText
                size="sm"
                fontColor="#5D7083"
                title={`${order}`}
                noLineClamp
              />
            </Box>
          </Box>

          <FlexCol
            sx={{
              justifyContent: 'space-between !important'
            }}>
            <FlexCol
              sx={{
                flex: 1,
                gridGap: '6px !important'
              }}>
              <SHighlightText
                value={`${productName || desc || '-'}`}
                searchQuery={search}
                maxLines={3}
                fontColor="#333D47"
                size="md"
                weight="bold"
              />

              <Box display="flex" gridGap={12} alignItems="center">
                <SHighlightText
                  value={`SKU: ${competitorSku ? `#${competitorSku}` : '-'}`}
                  searchQuery={search}
                  size="sm"
                  maxLines={1}
                  fontColor="#5D7083"
                />

                <Divider flexItem orientation="vertical" />

                <SHighlightText
                  value={`MFG: ${manufacturer || ''} ${manufacturerSku ? `(${manufacturerSku})` : '-'}`}
                  searchQuery={search}
                  size="sm"
                  maxLines={1}
                  fontColor="#5D7083"
                />
              </Box>

              {(!!unitPrice || !!quantity) && (
                <Box display="flex" gridGap={12} alignItems="center">
                  {!!unitPrice && (
                    <SHighlightText
                      value={`Price: ${Number(unitPrice) ? `${valueToMonetary(Number(unitPrice))}` : unitPrice || '-'}`}
                      searchQuery={search}
                      size="sm"
                      maxLines={1}
                      weight="bold"
                      fontColor="#333D47"
                    />
                  )}

                  {!!unitPrice && !!quantity && (
                    <Divider flexItem orientation="vertical" />
                  )}

                  {!!quantity && (
                    <SHighlightText
                      value={`Qty: ${quantity || '-'}`}
                      searchQuery={search}
                      size="sm"
                      maxLines={1}
                      weight="bold"
                      fontColor="#333D47"
                    />
                  )}
                </Box>
              )}

              {!!desc?.trim() &&
                !!productName?.trim() &&
                desc !== productName && (
                  <Box flex={1}>
                    <SHighlightText
                      fontColor="#333D47"
                      prefix={
                        <b
                          style={{
                            color: '#20262D',
                            marginRight: 4
                          }}>
                          Description:
                        </b>
                      }
                      size="sm"
                      maxLines={2}
                      lineHeight="20px"
                      searchQuery={search}
                      value={desc}
                    />
                  </Box>
                )}
            </FlexCol>

            {(!!fileSource || !!originalOrderNo) && (
              <>
                <Divider style={{ marginTop: 8, marginBottom: 8 }} />
                <Box display="flex" flexDirection="column" gridGap={4}>
                  {!!fileSource && (
                    <SText
                      fontColor="#5D7083"
                      prefix={
                        <b
                          style={{
                            color: '#5D7083',
                            marginRight: 4
                          }}>
                          File source:
                        </b>
                      }
                      size="sm"
                      maxLines={1}
                      title={fileSource}
                    />
                  )}

                  {!!originalOrderNo && (
                    <SText
                      fontColor="#5D7083"
                      prefix={
                        <b
                          style={{
                            color: '#5D7083',
                            marginRight: 4
                          }}>
                          Original order No:
                        </b>
                      }
                      size="sm"
                      maxLines={1}
                      title={`${originalOrderNo}`}
                    />
                  )}
                </Box>
              </>
            )}

            <Box
              style={{
                height: 56,
                display: 'flex',
                alignItems: 'center',
                borderTop: '1px solid #EEF0F2'
              }}>
              {params?.enableGettingCompetitorProductDescription && (
                <SButton
                  variant="text"
                  color="primary"
                  disabled={!summary?.specifications?.length}
                  onClick={() =>
                    openDialog(
                      <ProductSpecifications
                        title="AI Findings"
                        modalDescription="Based on my understanding of this product, Please see the details below"
                        onClose={closeDialog}
                        name={productName}
                        attributes={summary?.specifications}
                      />
                    )
                  }
                  style={{
                    ...iconStyle
                  }}>
                  AI Findings
                </SButton>
              )}

              <Tooltip
                arrow
                title="Specifications for this product are not available"
                disableHoverListener={!!customAttributes?.length}>
                <Box>
                  <SButton
                    variant="text"
                    color="primary"
                    disabled={!customAttributes?.length}
                    onClick={() => {
                      openDialog(
                        <ProductSpecifications
                          title="Additional Details from Uploaded File"
                          onClose={closeDialog}
                          name={productName || desc}
                          attributes={customAttributes}
                        />
                      );

                      captureAnalytics(
                        {
                          action: 'view',
                          subAction: 'Additional Product Details',
                          id: matched?.sku || ''
                        },
                        AnalyticsModules.AutoRFQDetails
                      );
                    }}
                    style={iconStyle}>
                    Additional Product Details
                  </SButton>
                </Box>
              </Tooltip>
            </Box>
          </FlexCol>
        </Box>
      </FlexRow>
    </Grid>
  );
};

export default Competitor;
